import React, { useEffect, useState } from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Alert, Platform, ScrollView, Linking } from 'react-native';
import AppTextInput from './AppTextInput';
import {
  clearPayPalDirectUrl,
  getSavedPayPalDirectUrl,
  savePayPalDirectUrl,
} from '../services/paypalDirectPaymentService';
import { maskPayPalDirectUrl } from '../utils/paypalDirectPayment';
import {
  savePaypalApiCredentials,
  getPaypalApiCredentialsStatus,
  removePaypalApiCredentials,
} from '../services/paypalApiCredentialsService';
import {
  SELLER_PAYPAL_SETUP_HELP,
  SELLER_PAYPAL_CONFIRM_HELP,
} from '../constants/paymentHelpText';

const showUserAlert = (title: string, message: string) => {
  if (Platform.OS === 'web') {
    (globalThis as { alert?: (msg: string) => void }).alert?.(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message);
  }
};

const confirmUserAction = (
  title: string,
  message: string,
  onConfirm: () => void
) => {
  if (Platform.OS === 'web') {
    if (
      (globalThis as { confirm?: (msg: string) => boolean }).confirm?.(
        `${title}\n\n${message}`
      )
    ) {
      onConfirm();
    }
    return;
  }
  Alert.alert(title, message, [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Remove', style: 'destructive', onPress: onConfirm },
  ]);
};

interface PayPalDirectSetupModalProps {
  visible: boolean;
  businessId: string;
  onClose: () => void;
  onSaved: () => void;
}

const PayPalDirectSetupModal: React.FC<PayPalDirectSetupModalProps> = ({
  visible,
  businessId,
  onClose,
  onSaved,
}) => {
  const [input, setInput] = useState('');
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Advanced: real PayPal API verification (optional)
  const [showApiSetup, setShowApiSetup] = useState(false);
  const [apiClientId, setApiClientId] = useState('');
  const [apiClientSecret, setApiClientSecret] = useState('');
  const [apiConfigured, setApiConfigured] = useState(false);
  const [apiClientIdPreview, setApiClientIdPreview] = useState<string | null>(null);
  const [apiLoading, setApiLoading] = useState(false);

  useEffect(() => {
    if (!visible || !businessId) {
      return;
    }
    let cancelled = false;
    (async () => {
      const url = await getSavedPayPalDirectUrl(businessId);
      if (!cancelled) {
        setCurrentUrl(url);
        setInput('');
      }
      const status = await getPaypalApiCredentialsStatus(businessId);
      if (!cancelled) {
        setApiConfigured(status.configured);
        setApiClientIdPreview(status.clientIdPreview || null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, businessId]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const result = await savePayPalDirectUrl(businessId, input);
      if (!result.success) {
        showUserAlert('Invalid PayPal link', result.error || 'Could not save.');
        return;
      }
      setCurrentUrl(result.url || null);
      onSaved();
      const successMessage =
        'Customers will pay you directly on PayPal. BookAPT does not process or hold these payments.';
      if (Platform.OS === 'web') {
        showUserAlert('PayPal linked', successMessage);
        onClose();
      } else {
        Alert.alert('PayPal linked', successMessage, [{ text: 'OK', onPress: onClose }]);
      }
    } catch {
      showUserAlert('Error', 'Could not save PayPal link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    confirmUserAction(
      'Remove PayPal link?',
      'Customers will no longer see a Pay with PayPal option for your business.',
      async () => {
        setLoading(true);
        try {
          await clearPayPalDirectUrl(businessId);
          setCurrentUrl(null);
          setInput('');
          onSaved();
          onClose();
        } catch {
          showUserAlert('Error', 'Could not remove PayPal link. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleSaveApiCredentials = async () => {
    if (!apiClientId.trim() || !apiClientSecret.trim()) {
      showUserAlert('Missing info', 'Enter both the Client ID and Client Secret.');
      return;
    }
    setApiLoading(true);
    try {
      const result = await savePaypalApiCredentials(businessId, apiClientId.trim(), apiClientSecret.trim(), 'live');
      if (!result.success) {
        showUserAlert('Could not save', result.error || 'Please check the values and try again.');
        return;
      }
      setApiClientId('');
      setApiClientSecret('');
      const status = await getPaypalApiCredentialsStatus(businessId);
      setApiConfigured(status.configured);
      setApiClientIdPreview(status.clientIdPreview || null);
      showUserAlert('Connected', 'BookAPT can now verify PayPal payments actually completed before confirming a booking.');
    } catch {
      showUserAlert('Error', 'Could not save credentials. Please try again.');
    } finally {
      setApiLoading(false);
    }
  };

  const handleRemoveApiCredentials = () => {
    confirmUserAction(
      'Disconnect PayPal API?',
      'Bookings will fall back to trusting PayPal payments without automatic verification.',
      async () => {
        setApiLoading(true);
        try {
          await removePaypalApiCredentials(businessId);
          setApiConfigured(false);
          setApiClientIdPreview(null);
        } catch {
          showUserAlert('Error', 'Could not disconnect. Please try again.');
        } finally {
          setApiLoading(false);
        }
      }
    );
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <Text style={styles.title}>PayPal (direct to you)</Text>
            <Text style={styles.body}>{SELLER_PAYPAL_SETUP_HELP}</Text>
            <Text style={styles.bodySecondary}>{SELLER_PAYPAL_CONFIRM_HELP}</Text>

            {currentUrl ? (
              <Text style={styles.current}>
                Current: {maskPayPalDirectUrl(currentUrl)}
              </Text>
            ) : null}

            <AppTextInput
              style={styles.input}
              placeholder="e.g. you@business.com (recommended) or paypal.me/mybusiness"
              value={input}
              onChangeText={setInput}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType={Platform.OS === 'ios' ? 'url' : 'default'}
              editable={!loading}
            />

            <TouchableOpacity
              style={[styles.primary, loading && styles.disabled]}
              onPress={handleSave}
              disabled={loading || !input.trim()}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.primaryText}>Save PayPal link</Text>
              )}
            </TouchableOpacity>

            {currentUrl ? (
              <TouchableOpacity style={styles.remove} onPress={handleRemove} disabled={loading}>
                <Text style={styles.removeText}>Remove PayPal link</Text>
              </TouchableOpacity>
            ) : null}

            {/* Advanced: real payment verification via PayPal's own API */}
            <TouchableOpacity
              style={styles.advancedToggle}
              onPress={() => setShowApiSetup(!showApiSetup)}
            >
              <Text style={styles.advancedToggleText}>
                {showApiSetup ? '▾' : '▸'} Advanced: verify payments automatically {apiConfigured ? '(Connected)' : '(optional)'}
              </Text>
            </TouchableOpacity>

            {showApiSetup && (
              <View style={styles.advancedBox}>
                <Text style={styles.bodySecondary}>
                  By default, BookAPT trusts that a customer completed payment on PayPal — it can't verify this automatically.
                  Connecting your own PayPal API credentials lets BookAPT check that a payment actually went through before
                  confirming a booking.
                </Text>

                <Text style={styles.stepsTitle}>How to get your Client ID and Secret:</Text>
                <Text style={styles.stepLine}>1. Go to developer.paypal.com and log in with your existing PayPal Business account.</Text>
                <Text style={styles.stepLine}>2. Open "Apps & Credentials" in the left menu.</Text>
                <Text style={styles.stepLine}>3. Make sure you're on the "Live" tab (not Sandbox) — Sandbox won't accept real payments.</Text>
                <Text style={styles.stepLine}>4. Click "Create App," give it any name (e.g. "BookAPT"), and click Create.</Text>
                <Text style={styles.stepLine}>5. Copy the Client ID shown, then click "Show" next to Secret and copy that too.</Text>
                <Text style={styles.stepLine}>6. Paste both below. Your Secret is stored securely and is never shown again, including to you — save a copy somewhere safe first.</Text>

                <TouchableOpacity
                  onPress={() => Linking.openURL('https://developer.paypal.com/dashboard/applications/live')}
                  style={styles.linkButton}
                >
                  <Text style={styles.linkButtonText}>Open developer.paypal.com →</Text>
                </TouchableOpacity>

                {apiConfigured ? (
                  <>
                    <Text style={styles.current}>Connected: {apiClientIdPreview}</Text>
                    <TouchableOpacity style={styles.remove} onPress={handleRemoveApiCredentials} disabled={apiLoading}>
                      <Text style={styles.removeText}>Disconnect PayPal API</Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <>
                    <Text style={styles.fieldLabel}>Client ID</Text>
                    <AppTextInput
                      style={styles.input}
                      placeholder="e.g. AZDx...9fQ"
                      value={apiClientId}
                      onChangeText={setApiClientId}
                      autoCapitalize="none"
                      autoCorrect={false}
                      editable={!apiLoading}
                    />
                    <Text style={styles.fieldLabel}>Client Secret</Text>
                    <AppTextInput
                      style={styles.input}
                      placeholder="e.g. EOx7...k2Lp"
                      value={apiClientSecret}
                      onChangeText={setApiClientSecret}
                      autoCapitalize="none"
                      autoCorrect={false}
                      secureTextEntry
                      editable={!apiLoading}
                    />
                    <TouchableOpacity
                      style={[styles.primary, apiLoading && styles.disabled]}
                      onPress={handleSaveApiCredentials}
                      disabled={apiLoading || !apiClientId.trim() || !apiClientSecret.trim()}
                    >
                      {apiLoading ? (
                        <ActivityIndicator color="#fff" />
                      ) : (
                        <Text style={styles.primaryText}>Connect PayPal API</Text>
                      )}
                    </TouchableOpacity>
                  </>
                )}
              </View>
            )}

            <TouchableOpacity style={styles.cancel} onPress={onClose} disabled={loading || apiLoading}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    maxHeight: '85%',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111',
    marginBottom: 8,
  },
  body: {
    fontSize: 14,
    color: '#555',
    lineHeight: 20,
    marginBottom: 8,
  },
  bodySecondary: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
    marginBottom: 12,
  },
  current: {
    fontSize: 13,
    color: '#0070ba',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 10,
    padding: 12,
    fontSize: 16,
    marginBottom: 16,
  },
  primary: {
    backgroundColor: '#0070ba',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  primaryText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  disabled: {
    opacity: 0.6,
  },
  remove: {
    marginTop: 12,
    alignItems: 'center',
  },
  removeText: {
    color: '#c00',
    fontSize: 15,
  },
  cancel: {
    marginTop: 16,
    alignItems: 'center',
    paddingBottom: 8,
  },
  cancelText: {
    color: '#666',
    fontSize: 15,
  },
  advancedToggle: {
    marginTop: 20,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  advancedToggleText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  advancedBox: {
    backgroundColor: '#F8F9FA',
    borderRadius: 10,
    padding: 14,
    marginTop: 8,
  },
  stepsTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#333',
    marginTop: 4,
    marginBottom: 6,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: '#333',
    marginBottom: 6,
  },
  stepLine: {
    fontSize: 13,
    color: '#555',
    lineHeight: 19,
    marginBottom: 4,
  },
  linkButton: {
    marginTop: 4,
    marginBottom: 16,
  },
  linkButtonText: {
    color: '#0070ba',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default PayPalDirectSetupModal;
